import React from 'react';
import { Bell, BellDot, BellOff, Eye, EyeOff, Clock, Hash, Tag } from 'lucide-react';
import { SectionCard, MetricCard, TabLoading, TabError, TabEmpty, StatusBadge, getGenericStatusVariant } from './shared';
import { useUserNotifications } from '../../hooks/useUserTabs';
import { cn, formatDateTime, formatRelativeTime, formatNumber } from '../../utils/helpers';

interface UserNotificationItem {
  id: number;
  title: string;
  body: string;
  type: string;
  isRead: boolean;
  readAt: string | null;
  createdAt: string;
  data?: Record<string, unknown>;
}

interface NotificationsTabProps {
  userId: number;
}

const NotificationsTab: React.FC<NotificationsTabProps> = ({ userId }) => {
  const { items, total, loading, error, refetch } = useUserNotifications(userId, true);

  if (loading) return <TabLoading />;
  if (error) return <TabError message={error} onRetry={refetch} />;
  if (!items || items.length === 0) return <TabEmpty message="No notifications found" icon={Bell} />;

  const typedItems = items as UserNotificationItem[];
  const readCount = typedItems.filter((n) => n.isRead).length;
  const unreadCount = typedItems.length - readCount;

  return (
    <div className="space-y-5">
      {/* Metrics */}
      <div className="grid grid-cols-3 gap-4">
        <MetricCard
          label="Total Notifications"
          value={formatNumber(total)}
          icon={Bell}
          color="text-blue-400"
          bgColor="bg-blue-500/10"
        />
        <MetricCard
          label="Read"
          value={formatNumber(readCount)}
          icon={Eye}
          color="text-emerald-400"
          bgColor="bg-emerald-500/10"
        />
        <MetricCard
          label="Unread"
          value={formatNumber(unreadCount)}
          icon={EyeOff}
          color="text-amber-400"
          bgColor="bg-amber-500/10"
        />
      </div>

      {/* Notification List */}
      <SectionCard
        title="Notifications"
        icon={BellDot}
        subtitle={`${formatNumber(total)} total`}
      >
        <div className="space-y-2.5">
          {typedItems.map((notification) => (
            <div
              key={notification.id}
              className={cn(
                'rounded-lg border border-border/50 p-4 transition-colors',
                notification.isRead ? 'bg-muted/5' : 'bg-primary/[0.03] border-primary/20'
              )}
            >
              <div className="flex items-start gap-3">
                {/* Read/Unread Indicator */}
                <div className="mt-1.5 flex-shrink-0">
                  {notification.isRead ? (
                    <BellOff className="h-4 w-4 text-muted-foreground/40" />
                  ) : (
                    <div className="relative">
                      <Bell className="h-4 w-4 text-primary" />
                      <span className="absolute -top-0.5 -right-0.5 h-2 w-2 rounded-full bg-primary animate-pulse" />
                    </div>
                  )}
                </div>

                {/* Content */}
                <div className="flex-1 min-w-0">
                  <div className="flex items-start justify-between gap-2">
                    <p className={cn(
                      'text-sm text-foreground truncate',
                      !notification.isRead ? 'font-semibold' : 'font-medium'
                    )}>
                      {notification.title}
                    </p>
                    <span className="text-[10px] text-muted-foreground font-mono flex-shrink-0">
                      #{notification.id}
                    </span>
                  </div>

                  <p className="text-xs text-muted-foreground mt-1 line-clamp-2">
                    {notification.body}
                  </p>

                  {/* Meta Row */}
                  <div className="flex items-center gap-3 mt-2.5 flex-wrap">
                    <StatusBadge
                      status={notification.type.replace(/_/g, ' ')}
                      variant={getGenericStatusVariant(notification.type)}
                    />

                    <div className="flex items-center gap-1 text-[11px] text-muted-foreground">
                      <Clock className="h-3 w-3" />
                      <span>{formatRelativeTime(notification.createdAt)}</span>
                    </div>

                    {notification.isRead && notification.readAt && (
                      <div className="flex items-center gap-1 text-[11px] text-emerald-400/70">
                        <Eye className="h-3 w-3" />
                        <span>Read {formatRelativeTime(notification.readAt)}</span>
                      </div>
                    )}

                    {!notification.isRead && (
                      <span className="text-[11px] font-medium text-primary">Unread</span>
                    )}
                  </div>
                </div>
              </div>
            </div>
          ))}
        </div>
      </SectionCard>
    </div>
  );
};

export default NotificationsTab;
